import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { DateTimeSelectorProps } from './types';

const DateTimeSelector: React.FC<DateTimeSelectorProps> = ({
  selectedDate,
  onDateChange,
  availableTimes,
  onTimeSelect,
}) => {
  const shouldReduceMotion = useReducedMotion();

  const containerVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1] as any,
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 10 },
    visible: { opacity: 1, y: 0 },
  };

  // Get today's date in YYYY-MM-DD format (EST timezone)
  const today = new Date();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = e.target.value;
    const selected = new Date(selectedValue + 'T00:00:00');
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    // Only allow today or future dates
    if (selected >= now) {
      onDateChange(new Date(selectedValue));
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col bg-white dark:bg-[#252527] 
      p-4 sm:p-5 md:p-6 
      rounded-2xl shadow-sm"
    >
      {/* ---------------- Date Picker ---------------- */}
      <motion.div variants={itemVariants} className="mb-6">
        <label className="block text-gray-700 dark:text-gray-300 
        font-semibold mb-2 sm:mb-3 
        text-base sm:text-lg">
          Select Date
        </label>

        <motion.input
          whileFocus={{ scale: shouldReduceMotion ? 1 : 1.02 }}
          type="date"
          value={selectedDate.toISOString().split('T')[0]}
          onChange={handleDateChange}
          min={todayString}
          className="w-full px-4 py-3 
          border-2 border-gray-300 dark:border-gray-600 
          rounded-xl 
          focus:ring-2 focus:ring-primary focus:border-primary
          dark:bg-gray-700 dark:text-white
          text-sm sm:text-base md:text-lg
          transition-all
          cursor-pointer
          [&::-webkit-calendar-picker-indicator]:dark:invert
          [&::-webkit-calendar-picker-indicator]:cursor-pointer"
        />
      </motion.div>

      {/* ---------------- Time Slots ---------------- */}
      <motion.div variants={itemVariants}>
        <label
          className="block text-gray-700 dark:text-gray-300 
          font-semibold mb-2 sm:mb-3 
          text-base sm:text-lg"
        >
          Time Slots
        </label>

        {availableTimes.length > 0 ? (
          <div
            className="grid 
            grid-cols-[repeat(auto-fit,minmax(100px,1fr))]
            gap-2 sm:gap-3
            max-h-[160px] sm:max-h-[200px] md:max-h-[240px]
            overflow-y-auto p-1"
          >
            {availableTimes.map((time) => (
              <motion.button
                key={time}
                variants={itemVariants}
                whileHover={{
                  scale: shouldReduceMotion ? 1 : 1.05,
                }}
                whileTap={{ scale: 0.96 }}
                transition={{ duration: 0.2 }}
                onClick={() => onTimeSelect(time)}
                className="
                  py-2 sm:py-2.5 px-3
                  bg-gray-100 dark:bg-gray-600
                  hover:bg-primary hover:text-white
                  text-gray-900 dark:text-white
                  rounded-xl
                  border-2 border-transparent
                  hover:border-primary
                  font-medium
                  text-xs sm:text-sm md:text-base
                  transition-all
                  focus:outline-none focus:ring-2 focus:ring-primary
                "
              >
                {time}
              </motion.button>
            ))}
          </div>
        ) : (
          <motion.div
            variants={itemVariants}
            className="text-center text-gray-500 
            py-8 sm:py-10
            bg-gray-50 dark:bg-[#252527]
            rounded-xl text-sm sm:text-base"
          >
            <span className="material-symbols-outlined text-6xl sm:text-7xl mb-4 block text-gray-400 dark:text-gray-500">
              event_busy
            </span>
            No available times for this date.
            <br className="hidden sm:block" />
            Please select another date.
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default DateTimeSelector;
